import { useEffect, useState } from 'react';
import { accessProvider } from '@/access';
import { AccessUnavailablePage } from './AccessUnavailablePage';
import { NoSessionPage } from './NoSessionPage';
import { NotEntitledPage } from './NotEntitledPage';

type GateState = 'pending' | 'anonymous' | 'unentitled' | 'unavailable';

/**
 * Wired as `<Admin loginPage={...}>` in `App.tsx` — react-admin renders this
 * whenever `authProvider.checkAuth` rejects (see `src/providers/authProvider.ts`),
 * which happens for both an actual "no session" and an authenticated session
 * that lacks this application's entitlement role. `checkAuth` itself cannot
 * tell those two apart from its own `resolvePrincipal`-shaped return value
 * (by design — see `src/access/types.ts`), so this gate asks the access
 * provider directly, through the same seam every other consumer uses
 * (`accessProvider`, never a concrete provider), and renders the matching,
 * distinct screen. Renders nothing while the check is in flight rather than
 * flashing one screen before swapping to the other.
 *
 * `unavailable` (wave B6) is a third, distinct fact from both of the above:
 * the access check itself could not be completed (see
 * `src/access/federatedAccessProvider.ts`'s own `.catch` and
 * `AccessUnavailablePage`) — never collapsed into `anonymous` any more, and
 * the outer `.catch` below (a provider throwing synchronously, or its
 * promise rejecting outside its own documented `AccessState` contract) is
 * treated the same way, for the same reason: any failure to determine
 * access is "could not complete the check", never silently "not signed in".
 */
export function AccessGatePage() {
  const [state, setState] = useState<GateState>('pending');

  useEffect(() => {
    let cancelled = false;
    accessProvider
      .resolveAccessState()
      .then((result) => {
        if (cancelled) return;
        if (result.kind === 'unentitled' || result.kind === 'unavailable') {
          setState(result.kind);
          return;
        }
        setState('anonymous');
      })
      .catch(() => {
        if (!cancelled) setState('unavailable');
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (state === 'pending') return null;
  if (state === 'unentitled') return <NotEntitledPage />;
  if (state === 'unavailable') return <AccessUnavailablePage />;
  return <NoSessionPage />;
}
